import { Component, ElementRef, HostBinding, HostListener, inject, OnInit, Type, ViewChild, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { catchError, from, Observable, of, switchMap, take } from 'rxjs';
import { MsalService } from '@azure/msal-angular';
import { AuthCodeMSALBrowserAuthenticationProvider } from '@microsoft/microsoft-graph-client/lib/es/src/authentication/msal-browser';
import { InteractionType, PublicClientApplication } from '@azure/msal-browser';
import { Client } from '@microsoft/microsoft-graph-client';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { PopupComponent } from '../popups/popup/popup.component';
import { ConfirmationComponent } from '../popups/confirmation/confirmation.component';
import { ConfirmationModel } from '../../models/confirmation.model';
import { ConfirmationButton } from '../../models/confirmation-button.model';
import { ConfirmationButtonType } from '../popups/confirmation/confirmation-button-type.enum';
import { AssignmentService } from '../../services/assignment.service';
import { environment } from '../../environments/environment';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-right-side-menu',
  standalone: true,
  imports: [SvgIconComponent, PopupComponent, TranslocoDirective],
  templateUrl: './right-side-menu.component.html',
  styleUrl: './right-side-menu.component.scss'
})
export class RightSideMenuComponent implements OnInit {
  protected assignmentService = inject(AssignmentService);
  private router = inject(Router);
  private authService = inject(MsalService);
  private destroyRef = inject(DestroyRef);
  private translocoService = inject(TranslocoService);

  @ViewChild(PopupComponent) popupComponent!: PopupComponent;
  @ViewChild('panel') panelRef!: ElementRef<HTMLDivElement>;

  @HostBinding('class.open') isOpen = false;
  @HostBinding('class.dragging') isDragging = false;
  @HostBinding('class.interactive') get isInteractive(): boolean {
    return this.isOpen || this.popupVisible;
  }
  @HostBinding('style.--menu-transform') get menuTransformStyle(): string {
    return this.menuTransform;
  }

  protected popupVisible: boolean = false;
  protected dynamicComponent: Type<any> = Object;
  protected popupData: any;
  protected userName: string = '';
  protected userGroup: string = '';
  protected imageData: string = '';

  private readonly swipeDistanceThreshold = 42;
  private readonly swipeEdgeThreshold = 28;
  private touchStartX = 0;
  private touchStartY = 0;
  private touchGesture: 'open' | 'close' | null = null;
  private suppressClickUntil = 0;
  private panelWidth = 250;
  private menuTransform = 'translateX(100%)';

  ngOnInit() {
    this.userName = this.getUserName();
    this.userGroup = this.getUserGroup();

    this.getProfileImage()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((imageData) => (this.imageData = imageData));
  }

  public open(e: MouseEvent) {
    e.stopPropagation();
    this.isOpen = true;
    this.isDragging = false;
    this.setMenuTransform('translateX(0)');
  }

  public close() {
    this.isOpen = false;
    this.isDragging = false;
    this.setMenuTransform('translateX(100%)');
  }

  public onLogoutClick() {
    this.executeMenuAction(() => this.openLogoutPopup());
  }

  public onStartAuditClick() {
    this.executeMenuAction(() => this.navigate('audit-setup'));
  }

  public onResumeAuditClick() {
    this.executeMenuAction(() => {
      const activeAuditId = this.assignmentService.auditInProgress()[0].id;
      this.assignmentService.getTaskLayoutId(activeAuditId)
        .pipe(take(1))
        .subscribe((taskLayoutId) => this.navigate(`audit/${taskLayoutId}`));
    });
  }

  public onHomeClick() {
    this.executeMenuAction(() => this.navigate(''));
  }

  public onMyAssignmentsClick() {
    this.executeMenuAction(() => this.navigate('assignments'));
  }

  public onFeedbackClick() {
    this.executeMenuAction(() => console.log('feedback'));
  }

  public onUserGuideClick() {
    this.executeMenuAction(() => {
      window.open('https://bp-lablet.emea.bosch.com/Home/User_Manual_FiveS_Mobil', '_blank');
    });
  }

  public onSettingsClick() {
    this.executeMenuAction(() => this.navigate('settings'));
  }

  @HostListener('document:touchstart', ['$event'])
  public onDocumentTouchStart(event: TouchEvent) {
    const touch = event.touches[0];
    if (!touch) {
      this.touchGesture = null;
      return;
    }

    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.panelWidth = this.panelRef?.nativeElement.getBoundingClientRect().width ?? this.panelWidth;

    if (this.isOpen) {
      this.touchGesture = 'close';
      this.isDragging = true;
      return;
    }

    this.touchGesture = window.innerWidth - touch.clientX <= this.swipeEdgeThreshold ? 'open' : null;
    this.isDragging = this.touchGesture === 'open';

    if (this.touchGesture === 'open') {
      this.setMenuTransform(`translateX(${this.panelWidth}px)`);
    }
  }

  @HostListener('document:touchmove', ['$event'])
  public onDocumentTouchMove(event: TouchEvent) {
    if (!this.touchGesture) return;

    const touch = event.touches[0];
    if (!touch) return;

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();

      if (this.touchGesture === 'open') {
        this.setMenuTransform(`translateX(${this.panelWidth - Math.max(0, -deltaX)}px)`);
      } else if (this.touchGesture === 'close') {
        this.setMenuTransform(`translateX(${Math.min(this.panelWidth, Math.max(0, deltaX))}px)`);
      }
    }
  }

  @HostListener('document:touchend', ['$event'])
  public onDocumentTouchEnd(event: TouchEvent) {
    if (!this.touchGesture) return;

    const touch = event.changedTouches[0];
    if (!touch) {
      this.touchGesture = null;
      return;
    }

    const deltaX = touch.clientX - this.touchStartX;
    const deltaY = touch.clientY - this.touchStartY;
    const isHorizontalSwipe = Math.abs(deltaX) > this.swipeDistanceThreshold && Math.abs(deltaX) > Math.abs(deltaY);

    if (isHorizontalSwipe) {
      if (this.touchGesture === 'open' && deltaX < 0) {
        this.isOpen = true;
        this.setMenuTransform('translateX(0)');
        this.suppressClickUntil = Date.now() + 250;
      }

      if (this.touchGesture === 'close' && deltaX > 0) {
        this.close();
        this.suppressClickUntil = Date.now() + 250;
      }
    } else {
      this.setMenuTransform(this.isOpen ? 'translateX(0)' : 'translateX(100%)');
    }

    this.isDragging = false;
    this.touchGesture = null;
  }

  @HostListener('document:touchcancel')
  public onDocumentTouchCancel() {
    this.isDragging = false;
    this.setMenuTransform(this.isOpen ? 'translateX(0)' : 'translateX(100%)');
    this.touchGesture = null;
  }

  private executeMenuAction(action: () => void): void {
    if (Date.now() < this.suppressClickUntil) return;
    this.close();
    action();
  }

  private navigate(route: string): void {
    this.router.navigate([route]);
  }

  private setMenuTransform(value: string) {
    this.menuTransform = value;
  }

  private openLogoutPopup() {
    this.dynamicComponent = ConfirmationComponent;
    this.popupVisible = true;

    this.popupData = new ConfirmationModel({
      imagePath: 'assets/log-out.svg',
      prompt: this.translocoService.translate('POPUPS.LOGOUT.TITLE'),
      hint: this.translocoService.translate('POPUPS.LOGOUT.HINT'),
      buttons: [
        new ConfirmationButton({
          text: this.translocoService.translate('COMMON.LABEL.CANCEL'),
          type: ConfirmationButtonType.Cancel
        }),
        new ConfirmationButton({
          text: this.translocoService.translate('POPUPS.LOGOUT.LOGOUT'),
          type: ConfirmationButtonType.SecondaryConfirm
        })
      ]
    });

    this.popupComponent.confirmed.pipe(take(1)).subscribe((confirmed: boolean) => {
      if (confirmed) this.logout(true);
    });
  }

  private logout(popup?: boolean): void {
    if (popup) {
      this.authService.logoutPopup({ mainWindowRedirectUri: '/login' });
    } else {
      this.authService.logoutRedirect();
    }
  }

  private getUserName(): string {
    const claims = this.authService.instance.getActiveAccount()?.idTokenClaims;
    return claims ? `${claims['family_name'] ?? ''} ${claims['given_name'] ?? ''}`.trim() : '';
  }

  private getUserGroup(): string {
    const regex = /\(([^)]*)\)/;
    const nameClaim = this.authService.instance.getActiveAccount()?.idTokenClaims?.name ?? '';
    return regex.exec(nameClaim)?.[1] ?? '';
  }

  protected getProfileImage(userId?: string): Observable<string> {
    const url = userId ? `/users/${userId}/photos/48x48/$value` : '/me/photos/48x48/$value';

    const authProvider = new AuthCodeMSALBrowserAuthenticationProvider(
      this.authService.instance as PublicClientApplication,
      {
        account: this.authService.instance.getActiveAccount()!,
        interactionType: InteractionType.Silent,
        scopes: environment.graphApiScope
      }
    );

    const graphClient = Client.initWithMiddleware({ authProvider });

    return from(graphClient.api(url).get() as Promise<Blob>).pipe(
      switchMap((blob) => {
        return new Observable<string>((observer) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            observer.next(reader.result as string);
            observer.complete();
          };
          reader.onerror = (err) => observer.error(err);
          reader.readAsDataURL(blob);
        });
      }),
      catchError(() => of('/assets/goat.png'))
    );
  }
}
